import React, { useState } from "react";
import { useAccount } from "../contexts/AccountContext";
import { useI18n } from "../i18n";

export default function AccountSelector() {
  const { t } = useI18n();
  const { 
    accounts, 
    selectedAccountId, 
    selectedAccount, 
    setSelectedAccountId, 
    loading 
  } = useAccount();
  
  const [isOpen, setIsOpen] = useState(false);

  if (loading || !accounts || accounts.length === 0) {
    return null;
  }

  const handleAccountChange = (accountId) => {
    setSelectedAccountId(accountId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Main Container */}
      <div className="bg-gradient-to-r from-green-50 to-white border border-green-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Business Account</h3>
            <p className="text-xs text-gray-500">Choose which business to create orders and expenses for</p>
          </div>
        </div>

        {/* Account Selector */}
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between p-3 bg-white border border-green-200 rounded-lg hover:border-green-300 hover:shadow-sm transition-all duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <div className="text-left">
                <div className="text-sm font-medium text-gray-900">
                  {selectedAccount?.name || 'Select Account'}
                </div>
                <div className="text-xs text-gray-500">
                  {selectedAccount?.description || 'Choose a business unit'}
                </div>
              </div>
            </div>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-green-200 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
              <div className="py-2">
                {accounts.map((account, index) => (
                  <button
                    key={account.accountId}
                    onClick={() => handleAccountChange(account.accountId)}
                    className={`w-full text-left px-4 py-3 hover:bg-green-50 transition-colors duration-150 ${
                      selectedAccountId === account.accountId 
                        ? 'bg-green-50 border-l-4 border-green-500' 
                        : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        selectedAccountId === account.accountId ? 'bg-green-500' : 'bg-gray-300'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{account.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {account.description || `Business Account #${index + 1}`}
                        </div>
                      </div>
                      {selectedAccountId === account.accountId && (
                        <svg className="w-4 h-4 text-green-500 ml-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
